import type {
  HovSkillResolvedEffects,
  MhovNHovGrowthExponentBonus,
} from "@/features/hoverIdle/hov/hovSkillEffects";
import type { HovAmplifierEntryId } from "@/features/hoverIdle/hov/hovAmplifierModel";
import {
  composeMpmUnlockAdoption,
  type MpmUnlockIntent,
} from "../mpm/mpmUnlockAdoption";
import type { MpmState } from "../mpm/mpmState";
import {
  clearActiveWallRunInState,
  type WallState,
} from "@/features/hoverIdle/wall/wallState";
import {
  compareHuge,
  isNonNegativeHuge,
  type HugeNumber,
} from "@/shared/numbers";
import { assertFiniteNonNegative } from "./hoverIdleStateValidation";
import type { HovGainEffectSourceIntent } from "./hovGainEffectComposer";

// Effect adoption boundary for resolved Hov skill effects. Given the resolved
// effects and the current model state groups, this module returns the next
// state groups the HoverIdleModel should adopt. The applier owns the per-
// effect patch rules; the model keeps authoritative state ownership, sync
// timing, and orchestration with Hov gain pipeline / HnG rank / Amplifier /
// Wall / MPM subsystems.

export interface HovSkillEffectApplierInput {
  readonly effects: HovSkillResolvedEffects;
  readonly mpmState: MpmState;
  readonly mpCapacity: number;
  readonly mpCapacityInitialized: boolean;
  readonly wallState: WallState;
}

export interface HovSkillGainBonusAdoption {
  readonly hovGainPerSecond: HugeNumber;
  readonly timGainPerSecond: HugeNumber;
}

export interface HovSkillAmplifierGainAdoption {
  readonly oHovMultiplierTargets: ReadonlySet<HovAmplifierEntryId>;
  readonly fixedMultipliersByEntry: ReadonlyMap<
    HovAmplifierEntryId,
    HugeNumber
  >;
  readonly clearedWallKindMultiplierTargets: ReadonlySet<HovAmplifierEntryId>;
  readonly oHovRunElapsedMultiplierTargets: ReadonlySet<HovAmplifierEntryId>;
}

export interface HovSkillApExponentBonusAdoption {
  readonly apBoarExponentBonus: number;
  readonly apDeerExponentBonusAcquired: boolean;
  readonly apButterflyExponentBonusAcquired: boolean;
}

export interface HovSkillEffectAdoption {
  readonly gain: HovSkillGainBonusAdoption;
  readonly hovGainIntent: HovGainEffectSourceIntent;
  readonly amplifier: HovSkillAmplifierGainAdoption;
  readonly ohovHovCoreGainMultiplierUnlocked: boolean;
  readonly mpCapacityCoreOHovHovCoreGainMultiplierUnlocked: boolean;
  readonly freeMpCoreOHovHovCoreGainMultiplierUnlocked: boolean;
  readonly hngRankScaleBaseBonus: number;
  // Authoritative MHov / NHov growth-exponent bonuses from acquired skills.
  readonly growthExponentBonus: MhovNHovGrowthExponentBonus;
  // Sum of additive bonuses to the HG / HnG base simultaneous purchase count
  // contributed by acquired Hov skills. HoverIdleModel composes this with
  // Wall reward multipliers at the planner cap boundary.
  readonly hgHngSimultaneousPurchaseCountBaseBonus: number;
  readonly wallUnlocked: boolean;
  readonly explorationUnlocked: boolean;
  readonly wallRewardCapRelaxed: boolean;
  readonly wallState: WallState;
  readonly mpmUnlockIntent: MpmUnlockIntent;
  readonly mpmState: MpmState;
  readonly apExponent: HovSkillApExponentBonusAdoption;
}

function assertHugeFiniteNonNegative(value: HugeNumber, name: string): void {
  if (!value.isFinite() || !isNonNegativeHuge(value)) {
    throw new Error(`${name} must be a finite non-negative HugeNumber`);
  }
}

function assertHugeFinitePositive(value: HugeNumber, name: string): void {
  if (!value.isFinite() || compareHuge(value, 0) <= 0) {
    throw new Error(`${name} must be a finite positive HugeNumber`);
  }
}

function validateEffects(effects: HovSkillResolvedEffects): void {
  assertHugeFiniteNonNegative(
    effects.hovGainPerSecond,
    "effects.hovGainPerSecond",
  );
  assertHugeFiniteNonNegative(
    effects.timGainPerSecond,
    "effects.timGainPerSecond",
  );
  assertHugeFinitePositive(
    effects.hov1GainMultiplier,
    "effects.hov1GainMultiplier",
  );
  assertFiniteNonNegative(
    effects.bhgToHgPowerExponentBonus,
    "effects.bhgToHgPowerExponentBonus",
  );
  assertFiniteNonNegative(
    effects.hngRankScaleBaseBonus,
    "effects.hngRankScaleBaseBonus",
  );
  assertFiniteNonNegative(
    effects.hgHngSimultaneousPurchaseCountBaseBonus,
    "effects.hgHngSimultaneousPurchaseCountBaseBonus",
  );
  assertFiniteNonNegative(
    effects.apBoarExponentBonus,
    "effects.apBoarExponentBonus",
  );
  assertFiniteNonNegative(
    effects.growthExponentBonus.mhov,
    "effects.growthExponentBonus.mhov",
  );
  assertFiniteNonNegative(
    effects.growthExponentBonus.nHovHgAmplify,
    "effects.growthExponentBonus.nHovHgAmplify",
  );
  assertFiniteNonNegative(
    effects.growthExponentBonus.nHovHgNHovEffectsToHng,
    "effects.growthExponentBonus.nHovHgNHovEffectsToHng",
  );
  assertFiniteNonNegative(
    effects.growthExponentBonus.nHovHngScale,
    "effects.growthExponentBonus.nHovHngScale",
  );
}

function resolveHovGainIntent(
  effects: HovSkillResolvedEffects,
): HovGainEffectSourceIntent {
  return {
    bhgToHgPowerExponentBonus: effects.bhgToHgPowerExponentBonus,
    hov1GainMultiplier: effects.hov1GainMultiplier,
    hngRankScaleBaseBonus: effects.hngRankScaleBaseBonus,
  };
}

function resolveAmplifierAdoption(
  effects: HovSkillResolvedEffects,
): HovSkillAmplifierGainAdoption {
  return {
    oHovMultiplierTargets: new Set(effects.amplifierGainOHovMultiplierTargets),
    fixedMultipliersByEntry: new Map(
      effects.amplifierGainFixedMultipliersByEntry,
    ),
    clearedWallKindMultiplierTargets: new Set(
      effects.amplifierGainClearedWallKindMultiplierTargets,
    ),
    oHovRunElapsedMultiplierTargets: new Set(
      effects.amplifierGainOHovRunElapsedMultiplierTargets,
    ),
  };
}

function resolveMpmUnlockIntent(
  effects: HovSkillResolvedEffects,
): MpmUnlockIntent {
  return {
    hovGainUpgradesUnlocked: false,
    mhovExecutionUnlocked: effects.mhovMpExecutionUnlocked,
    amplifierPanelsUnlocked: effects.amplifierPanelMpPlacementUnlocked,
    amplifierUpgradesUnlocked: false,
    nhovExecutionUnlocked: effects.nhovMpExecutionAssignmentUnlocked,
    // OHov execution automation is granted by the outer-wall first-clear
    // reward, not by Hov skills.
    ohovExecutionUnlocked: false,
  };
}

function resolveWallState(input: HovSkillEffectApplierInput): WallState {
  if (input.effects.wallUnlocked) {
    return input.wallState;
  }
  return clearActiveWallRunInState(input.wallState);
}

export function applyHovSkillEffects(
  input: HovSkillEffectApplierInput,
): HovSkillEffectAdoption {
  const { effects } = input;
  validateEffects(effects);
  const mpmUnlockIntent = resolveMpmUnlockIntent(effects);
  return {
    gain: {
      hovGainPerSecond: effects.hovGainPerSecond,
      timGainPerSecond: effects.timGainPerSecond,
    },
    hovGainIntent: resolveHovGainIntent(effects),
    amplifier: resolveAmplifierAdoption(effects),
    ohovHovCoreGainMultiplierUnlocked:
      effects.ohovHovCoreGainMultiplierUnlocked,
    mpCapacityCoreOHovHovCoreGainMultiplierUnlocked:
      effects.mpCapacityCoreOHovHovCoreGainMultiplierUnlocked,
    freeMpCoreOHovHovCoreGainMultiplierUnlocked:
      effects.freeMpCoreOHovHovCoreGainMultiplierUnlocked,
    hngRankScaleBaseBonus: effects.hngRankScaleBaseBonus,
    growthExponentBonus: effects.growthExponentBonus,
    hgHngSimultaneousPurchaseCountBaseBonus:
      effects.hgHngSimultaneousPurchaseCountBaseBonus,
    wallUnlocked: effects.wallUnlocked,
    explorationUnlocked: effects.explorationUnlocked,
    wallRewardCapRelaxed: effects.wallRewardCapRelaxed,
    wallState: resolveWallState(input),
    mpmUnlockIntent,
    mpmState: composeMpmUnlockAdoption({
      state: input.mpmState,
      sources: [mpmUnlockIntent],
      mpCapacity: input.mpCapacity,
      mpCapacityInitialized: input.mpCapacityInitialized,
    }),
    apExponent: {
      apBoarExponentBonus: effects.apBoarExponentBonus,
      apDeerExponentBonusAcquired: effects.apDeerExponentBonusAcquired,
      apButterflyExponentBonusAcquired:
        effects.apButterflyExponentBonusAcquired,
    },
  };
}
